import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryOptions,
} from '@tanstack/react-query'
import { listPendingEvaluations } from 'api/admin/coach/listPendingEvaluations'
import { coachKeys } from './keys'

type UsePendingEvaluationsQueryData = Awaited<
  ReturnType<typeof listPendingEvaluations>
>
type UsePendingEvaluationsQueryParams = Parameters<
  typeof listPendingEvaluations
>[0]
type UsePendingEvaluationsQueryKey = ReturnType<
  typeof coachKeys.pendingEvaluations
>
type UsePendingEvaluationsQueryPageParam = string | undefined

type UsePendingEvaluationsQueryOptions = Partial<
  UseInfiniteQueryOptions<
    UsePendingEvaluationsQueryData,
    Error,
    InfiniteData<
      UsePendingEvaluationsQueryData,
      UsePendingEvaluationsQueryPageParam
    >,
    UsePendingEvaluationsQueryData,
    UsePendingEvaluationsQueryKey,
    UsePendingEvaluationsQueryPageParam
  >
>

export const usePendingEvaluations = (
  request: Omit<UsePendingEvaluationsQueryParams, 'cursor'>,
  options: UsePendingEvaluationsQueryOptions = {}
) => {
  const { timeframe, companyId, positionId, shiftId, pageSize } = request

  return useInfiniteQuery<
    UsePendingEvaluationsQueryData,
    Error,
    InfiniteData<
      UsePendingEvaluationsQueryData,
      UsePendingEvaluationsQueryPageParam
    >,
    UsePendingEvaluationsQueryKey,
    UsePendingEvaluationsQueryPageParam
  >({
    ...options,
    queryKey: coachKeys.pendingEvaluations({
      timeframe,
      companyId,
      positionId,
      shiftId,
      pageSize,
    }),
    initialPageParam: undefined,
    queryFn: ({ pageParam }) =>
      listPendingEvaluations({
        ...request,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.assessments.length === 0) {
        return undefined
      }
      return lastPage.nextCursor ?? undefined
    },
  })
}
